import { ClipboardCheck, Boxes, Wrench, ShieldCheck, Rocket, Target, HelpCircle, Hexagon, Palette } from "lucide-react";
import { EVAL_CAPABILITIES, EVAL_DISCOVERY, EVAL_GOVERNANCE, EVAL_MODULES } from "@/lib/partnership";

const Section = ({ icon: Icon, title, children, testId }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6" data-testid={testId}>
    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-zinc-800">
      <Icon className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
      <h3 className="text-sm font-semibold text-zinc-100">{title}</h3>
    </div>
    {children}
  </div>
);

export default function Evaluation() {
  return (
    <div className="space-y-6" data-testid="evaluation-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100 flex items-center gap-3">
          <ClipboardCheck className="w-7 h-7 text-emerald-400" strokeWidth={1.5} /> Partner Evaluation
        </h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
          A working brief for CRM technology providers assessing O.N.E.Tech as an embedded or standalone capability.
          Commercial and technical in scope — no legal promises, no pricing.
        </p>
      </div>

      <Section icon={Boxes} title="Available Modules" testId="eval-modules">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {EVAL_MODULES.map((mod) => (
            <div key={mod.key} className="border border-zinc-800 rounded-sm p-4 bg-zinc-950/50 hover:border-emerald-500/30 transition-colors duration-200" data-testid={`eval-module-${mod.key}`}>
              <div className="text-xs font-semibold text-emerald-400">{mod.name}</div>
              <p className="text-[11px] text-zinc-400 leading-relaxed mt-2">{mod.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-zinc-500 mt-4">Modules can be enabled independently per broker tenant, in both deployment modes.</p>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section icon={Wrench} title="Required Technical Capabilities (Partner CRM)" testId="eval-capabilities">
          <div className="space-y-2.5">
            {EVAL_CAPABILITIES.map((cap, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-zinc-300 leading-relaxed">
                <span className="w-4 h-4 shrink-0 rounded-sm bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center text-[9px] font-mono text-emerald-400 mt-0.5">{i + 1}</span>
                {cap}
              </div>
            ))}
          </div>
        </Section>

        <Section icon={ShieldCheck} title="Governance & Data-Sovereignty Controls" testId="eval-governance">
          <div className="space-y-2.5">
            {EVAL_GOVERNANCE.map((g, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-zinc-300 leading-relaxed">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> {g}
              </div>
            ))}
          </div>
        </Section>
      </div>

      <Section icon={Rocket} title="Deployment Options" testId="eval-deployment">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border border-zinc-800 rounded-sm p-4 bg-zinc-950/50">
            <div className="flex items-center gap-2 text-xs font-semibold text-zinc-100"><Hexagon className="w-3.5 h-3.5 text-emerald-400" /> Mode 1 — Standalone</div>
            <p className="text-[11px] text-zinc-400 leading-relaxed mt-2">
              O.N.E.Tech-branded secure portal with API/webhook connectors into the broker's stack. Fastest path for a
              single broker proof of value; the partner CRM remains the system of record via write-back.
            </p>
          </div>
          <div className="border border-zinc-800 rounded-sm p-4 bg-zinc-950/50">
            <div className="flex items-center gap-2 text-xs font-semibold text-zinc-100"><Palette className="w-3.5 h-3.5 text-emerald-400" /> Mode 2 — Embedded / White-Label</div>
            <p className="text-[11px] text-zinc-400 leading-relaxed mt-2">
              The same engine embedded as a partner-branded CRM module (configurable name, logo, colour), shipped in the
              partner's release train and activated per broker tenant. Suited to OEM-style partnerships.
            </p>
          </div>
        </div>
      </Section>

      <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-md p-6" data-testid="eval-first-usecase">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
          <h3 className="text-sm font-semibold text-emerald-400">Suggested First Validation Use Case</h3>
        </div>
        <p className="text-xs text-zinc-300 leading-relaxed max-w-3xl">
          <span className="font-semibold text-zinc-100">FTD recovery on a single broker book.</span> Connect CRM read/write and one
          communication channel in the synthetic environment, replay the FTD/no-answer and failed-payment scenarios, and measure
          queue prioritisation accuracy, governance gating and recovered-revenue attribution over a short validation window.
          This exercises the full pipeline — signal → decision → governance → approval → execution → write-back — with the
          smallest integration surface, before extending to KYC, Activation, Retention and Withdrawal Coordination.
        </p>
      </div>

      <Section icon={HelpCircle} title="Questions Requiring Technical Discovery" testId="eval-discovery">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5">
          {EVAL_DISCOVERY.map((q, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-zinc-300 leading-relaxed">
              <span className="font-mono text-emerald-500 text-[10px] mt-0.5 shrink-0">Q{i + 1}</span> {q}
            </div>
          ))}
        </div>
      </Section>

      <div className="border border-amber-500/20 bg-amber-500/5 rounded-sm px-4 py-3 text-[11px] font-mono text-amber-500/90" data-testid="evaluation-disclaimer">
        This evaluation brief makes no legal commitments, displays no pricing, and claims no existing integration with any named provider. All demonstrations run on synthetic data.
      </div>
    </div>
  );
}
